import { MantineProvider, v8CssVariablesResolver } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { Notifications } from "@mantine/notifications";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Suspense, useEffect, useState } from "react";

import { PageLoader } from "./layout/PageLoader";
import { theme } from "./theme";

import { WalkthroughProvider } from "@/app/layout/WalkthroughProvider";
import { defaultCurrency, getCurrency } from "@/lib/currency";
import { queryConfig } from "@/lib/react-query";
import { AuthLoader, useUser } from "@/services/auth";

import type { FC, PropsWithChildren } from "react";

const UserSettingsStorageSync = () => {
  const { data: user } = useUser();
  const [, setCurrency] = useLocalStorage({ key: "currency", defaultValue: defaultCurrency });
  const [, setCompact] = useLocalStorage({ key: "number-formatting", defaultValue: false });

  useEffect(() => {
    if (!user) return;

    setCurrency(getCurrency(user.additional_info?.currency) ?? defaultCurrency);
    setCompact((user.additional_info?.roundedValue ?? "full") === "compact");
  }, [user, setCurrency, setCompact]);

  return null;
};

export type AppProviderProps = PropsWithChildren;

export const AppProvider: FC<AppProviderProps> = ({ children }) => {
  const [queryClient] = useState(() => new QueryClient({ defaultOptions: queryConfig }));

  return (
    <MantineProvider theme={theme} cssVariablesResolver={v8CssVariablesResolver}>
      <Suspense fallback={<PageLoader />}>
        <QueryClientProvider client={queryClient}>
          {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
          <Notifications position="bottom-left" maw={400} pauseResetOnHover="notification" />
          <AuthLoader renderLoading={() => <PageLoader />}>
            <UserSettingsStorageSync />
            <WalkthroughProvider>{children}</WalkthroughProvider>
          </AuthLoader>
        </QueryClientProvider>
      </Suspense>
    </MantineProvider>
  );
};
